import { useEffect, useRef, useState } from "react";
import { useAuthStep } from "@/hooks/useAuthStep";
import { createClient } from "@supabase/supabase-js";
import { Check, Loader2 } from "lucide-react";
import DOB from "@/components/DOB";

import {
	Form,
} from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "@/lib/config";
import { PublicClientApplication, type Configuration } from "@azure/msal-browser";
import { OutlookSvg } from "@/lib/svg.tsx";
import { getAgeBand, trackEvent } from "@/lib/utils";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { useMicrosoftAuth } from "@/hooks/useMicrosoftAuth";

const formSchema = z.object({
	month: z.string().min(1, "Please select a month"),
	year: z.string().min(1, "Please select a year"),
});

const azureClientId = import.meta.env.VITE_AZURE_CLIENT_ID;
const azureTenantId = import.meta.env.VITE_AZURE_TENANT_ID; // optional
const supabase = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);

const OutlookAuth = () => {

	const { setAuthStep, setToken, uuid, setAgeBand, loginState, handleAuth } = useAuthStep();
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);

	const { microsoftLogin } = useMicrosoftAuth();


	const loginPrompt = async (data: any) => {

		if (new Date().getFullYear() - parseInt(data.year) < 13) {
			setAuthStep("underAge");
			return;
		}

		// Use MSAL popup (no redirect)
		const authFn = await microsoftLogin();
		handleAuth(authFn, "signup");
	};

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			month: "",
			year: "",
		},
	});

	// Function to check if DOB is valid and age is 13+
	const isDOBValid = () => {
		const formData = form.getValues();
		const month = formData.month;
		const year = formData.year;

		// Check if both month and year are selected
		if (!month || !year) {
			return false;
		}
		return true;
	};

	// Watch form changes to update button state
	useEffect(() => {
		const subscription = form.watch((value) => {
			setIsButtonDisabled(!isDOBValid());
		});
		return () => subscription.unsubscribe();
	}, [form]);

	const checkAgeRef = useRef<HTMLDivElement>(null);

	const checkAge = () => {

		console.log("checkAge", isButtonDisabled);

		if (isButtonDisabled) return;

		const { year, month } = form.getValues();

		if (new Date().getFullYear() - parseInt(year) < 13 || (new Date().getFullYear() - parseInt(year) === 13 && new Date().getMonth() + 1 < parseInt(month))) {
			setAuthStep("underAge");
			return;
		} else {
			checkAgeRef.current?.remove();
			setAgeBand(getAgeBand(parseInt(year)));
		}

	}

	return (
		<>
			<div className="px-[30px] text-gray-500">
				<h1 className="text-2xl font-semibold text-black mb-[5px]">Join now (takes 30 secs)</h1>
				<p>Over 1.5 million students made their Canvas better.</p>
				<div className="mt-[20px]">
					<Form {...form}>
						<form onSubmit={form.handleSubmit(loginPrompt)}>
							<DOB form={form} />
							<div className={`mt-4 relative ${isButtonDisabled ? "opacity-50 pointer-events-none" : ""}`} >
								<div ref={checkAgeRef} className="w-full h-full absolute top-0 left-0 z-10 bg-none" onMouseOver={checkAge} onMouseMove={() => { console.log("mouse move") }} />
								<button type="submit" className="w-full flex items-center justify-center gap-2 rounded-md border border-gray-300 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
									<OutlookSvg className="w-4 h-4" />
									Continue with Microsoft
								</button>
								{loginState.loading && <span className="text-gray-500 text-sm flex items-center gap-2 mt-2"><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</span>}
								{loginState.success && <span className="text-green-700 text-sm flex items-center gap-2 mt-2"><Check className="w-4 h-4" /> Success!</span>}
							</div>
						</form>
					</Form>
				</div>
				<div className="text-[11px] text-gray-500 mt-2 flex-1">
					By signing up, you acknowledge that you have read, understood, and agree to the BetterCampus <a href="/terms" target="_blank" className="text-blue-500">Terms</a> and <a href="/privacy" target="_blank" className="text-blue-500">Privacy Policy</a>
				</div>
			</div>
		</>
	)
}

export default OutlookAuth;
